import {FlowLang} from 'flow-to-typescript-codemod';
import type {Type} from '../DataTypeDefinition';
import type {DefaultBaseStyleConfig} from '@packages/systems/style/types';
import {noConstraints} from '@packages/systems/constraints-constants';
import {type NormalizedConstraints} from '@packages/systems/constraints-types';
import {
  type Expression,
  TTypeReference,
  TNoDataTypeProvided,
  EFunction,
  ERecord,
} from '@packages/systems/wfdl-ast';
import type {InternalComponent, ComponentShape} from './ComponentTypes';
import type {ComponentName} from './Names';
import {
  type Qualified,
  type IconName,
  type QualifiedMap,
  qualify,
  iconName,
  pluginName,
} from '@packages/systems/wfdl-Qualified';
import {reportBugOnce} from '@packages/utilities/reporter';
import {getFunction, getRecord} from '../Expressions';
import {getRecordFields} from '../Types';

export type StyleVariant = {
  id: string;
  displayName: string;
};

export type Variants = {
  [styleVariantFieldId: string]: StyleVariant[];
};

/**
 * A single multi-state preview scenario for a code component.
 *
 * Scenarios are a Designer-only convention: they describe one visual
 * configuration of a (stateful) code component for the component-canvas
 * multi-state preview grid. They are produced by the AICC generation flow
 * and stored on the component's `source.meta` (see {@link SourceMeta}).
 * As a result they are never shipped in the published bundle.
 */
export type ComponentPreviewScenario = {
  id: string;
  label: string;
  props?: Readonly<Record<string, unknown>>;
};

/**
 * Designer-only metadata derived from / about a code component's source.
 *
 * Lives alongside `code` on the {@link Source} object. The whole `source`
 * object round-trips through symbol serialization, so anything here
 * persists, but `projectComponentsToFiles` only emits `source.code`, so
 * `meta` is never shipped in the published bundle.
 */
export type SourceMeta = {
  /** The prebuilt template this component was created from. */
  templateId?: string;
  /** Initial branded source, used to recognize the template until its first edit. */
  templateSourceHash?: number;
  /**
   * The set of visual states the component can be in, produced by the AICC
   * generation flow. The Designer reads these to drive the multi-state
   * preview grid, the Navigator States section, and scenario seeding —
   * instead of parsing them out of the component code at render time.
   */
  states?: ComponentPreviewScenario[];
  /**
   * A hash of `source.code` at the moment `states` were last recorded.
   * Used by the Designer to detect when the code has changed since the
   * states were produced (a likely sign the states are stale) and prompt
   * the user to regenerate them.
   */
  statesSourceHash?: number;
};

/**
 * If a component is backed by code, it will have a source field.
 */
export type Source = {
  type: 'Site';
  framework: 'React';
  version: '19';
  code: string;
  /**
   * Designer-only metadata about the source (e.g. the component's preview states).
   */
  meta?: SourceMeta;
};

export type DismissedPropElementIds = string[];

const genericIcon = qualify(pluginName('Basic'), iconName('DivBlock'));

type ComponentDefaults = FlowLang.Diff<
  InternalComponent,
  {
    render: Expression;
  }
>;

export const defaults: ComponentDefaults = {
  constraints: noConstraints,
  dataType: TNoDataTypeProvided,
  contextType: TTypeReference('context'),
  displayName: 'Component',
  description: '',
  groupName: '',
  icon: genericIcon,
  style: undefined,
  origin: undefined,
  variants: undefined,
  source: undefined,
  dismissedPropElementIds: undefined,
  placeholderBaseVariantName: undefined,
  defaultData: EFunction({param: 'context', body: ERecord({})}),
};

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
/**
 * Opaque alias to InternalComponent structure.
 */
export type ComponentType = InternalComponent;

/**
 * Map of component types to their implementations.
 */
export type ComponentMap = QualifiedMap<ComponentName, ComponentType>;

/**
 * Function used to take a ComponentShape and turn it into a Component type.
 */
export function create(shape: ComponentShape): ComponentType {
  if (!shape.defaultData && !shape.dataType) {
    shape = {
      ...shape,
      defaultData: defaults.defaultData,
    };
  }

  const comp = createInternal(shape);

  reportDefaultDataMismatch(comp, 'create');
  return comp;
}

export function reify(shape: ComponentShape): ComponentType {
  const comp = createInternal(shape);

  reportDefaultDataMismatch(comp, 'reification');
  return comp;
}

function createInternal(shape: ComponentShape): ComponentType {
  return {
    render: shape.render,
    dataType: shape.dataType || defaults.dataType,
    contextType: shape.contextType || defaults.contextType,
    constraints: {...defaults.constraints, ...shape.constraints},
    displayName: shape.displayName || defaults.displayName,
    description: shape.description || defaults.description,
    groupName: shape.groupName || defaults.groupName,
    icon: shape.icon || defaults.icon,
    style: shape.style || defaults.style,
    origin: shape.origin || defaults.origin,
    variants: shape.variants || defaults.variants,
    source: shape.source || defaults.source,
    dismissedPropElementIds:
      shape.dismissedPropElementIds || defaults.dismissedPropElementIds,
    placeholderBaseVariantName:
      shape.placeholderBaseVariantName || defaults.placeholderBaseVariantName,
    // Omit when unset; getHiddenForNonComponentManagers treats absence as false.
    ...(shape.hiddenForNonComponentManagers
      ? {hiddenForNonComponentManagers: shape.hiddenForNonComponentManagers}
      : {}),
    defaultData: shape.defaultData,
  };
}

/**
 * Retrieve constraints from a ComponentType.
 */
export function getConstraints(comp: ComponentType): NormalizedConstraints {
  return comp.constraints;
}

/**
 * Retrieve dataType from a ComponentType.
 */
export function getDataType(comp: ComponentType): Type {
  return comp.dataType;
}

/**
 * Retrieve contextType from a ComponentType.
 */
export function getContextType(comp: ComponentType): Type {
  return comp.contextType;
}

/**
 * Retrieve displayName from a ComponentType.
 */
export function getDisplayName(comp: ComponentType): string {
  return comp.displayName;
}

/**
 * Retrieve description from a ComponentType.
 */
export function getDescription(comp: ComponentType): string {
  return comp.description;
}

/**
 * Retrieve group name from a ComponentType.
 */
export function getGroupName(comp: ComponentType): string {
  return comp.groupName;
}

/**
 * Retrieve icon from a ComponentType.
 */
export function getIcon(comp: ComponentType): Qualified<IconName> {
  return comp.icon;
}

/**
 * Retrieve the render Expression from a ComponentType.
 */
export function getRender(comp: ComponentType): Expression {
  return comp.render;
}

/**
 * Retrieve the defined styles for a ComponentType.
 */
export function getStyle(comp: ComponentType): DefaultBaseStyleConfig | null {
  return comp.style || null;
}

/**
 * Retrieve the defined origin for a ComponentType.
 */
export function getOrigin(
  comp: ComponentType
): Qualified<ComponentName> | undefined {
  return comp.origin;
}

/**
 * Retrieve the style variants for a ComponentType.
 */
export function getVariants(comp: ComponentType): Variants | undefined {
  return comp.variants;
}

/**
 * Retrieve the source for a ComponentType.
 */
export function getSource(comp: ComponentType): Source | undefined {
  return comp.source;
}

/**
 * Retrieve the dismissed prop element ids for a ComponentType.
 */
export function getDismissedPropElementIds(
  comp: ComponentType
): string[] | undefined {
  return comp.dismissedPropElementIds;
}

/**
 * Retrieve the placeholder base variant name for a ComponentType.
 */
export function getPlaceholderBaseVariantName(
  comp: ComponentType
): string | undefined {
  return comp.placeholderBaseVariantName;
}

/**
 * Retrieve the Designer-only multi-state preview scenarios for a
 * ComponentType, read from `source.meta.states`. Returns `undefined` when
 * the component has no source or no stored states.
 */
export function getPreviewScenarios(
  comp: ComponentType
): ComponentPreviewScenario[] | undefined {
  return comp.source?.meta?.states;
}

/**
 * Whether the component is hidden from users who can't manage components
 * (Build mode / Marketer roles). Defaults to false when unset.
 */
export function getHiddenForNonComponentManagers(comp: ComponentType): boolean {
  return comp.hiddenForNonComponentManagers ?? false;
}

export function getDefaultData(comp: ComponentType): Expression | undefined {
  return comp.defaultData;
}

/**
 * Immutably update a ComponentType with specified fields.
 */
export function update(
  comp: ComponentType,
  fields: Partial<ComponentType>
): ComponentType {
  let field: keyof ComponentType;
  for (field in fields) {
    if (fields[field] !== comp[field]) {
      const next = {
        ...comp,
        ...fields,
      };
      if (
        next.dataType !== comp.dataType ||
        next.defaultData !== comp.defaultData
      ) {
        reportDefaultDataMismatch(next, 'update');
      }
      return next;
    }
  }
  return comp;
}

function reportDefaultDataMismatch(
  comp: ComponentType,
  origin: 'create' | 'update' | 'reification'
): boolean {
  const {defaultData, dataType, displayName} = comp;
  if (!defaultData) return false;

  const dataTypeFields = getRecordFields(dataType);
  if (!dataTypeFields) return false;

  let recordExpr: Record<string, Expression> = {};
  const funcExpr = getFunction(defaultData);
  if (funcExpr) {
    recordExpr = getRecord(funcExpr.body) ?? recordExpr;
  }

  const extraFields = Object.keys(recordExpr).filter(
    (k) => !(k in dataTypeFields)
  );
  const missingFields = Object.keys(dataTypeFields).filter(
    (f) => !(f in recordExpr)
  );

  if (extraFields.length === 0 && missingFields.length === 0) {
    return false;
  }

  // eslint-disable-next-line webflow/better-bug-reporting -- ticket lives in Linear, not Jira.
  reportBugOnce(
    'ComponentDefaultDataMismatch',
    `defaultData keys diverge from dataType fields: ${displayName} +[${extraFields.join(',')}] -[${missingFields.join(',')}]`,
    {
      team: 'design-system',
      ticket: 'https://linear.app/webflow/issue/LOC-2636',
      origin,
      displayName,
      extraFields,
      missingFields,
    },
    {severity: 'warning'}
  );

  return true;
}
